import * as React from "react";
import uiString from "../../components/main-view/string.json";
import {
  faHome,
  faFileSignature,
  faInfoCircle,
  faCog,
  faChartPie,
  faCar,
  faClipboardList
} from "@fortawesome/free-solid-svg-icons";
import { WebMenuAuthList } from "../use-check-web-auth-fn";
import routes from "../../routes";
import { Role } from "../../user-types";
import { useSelector } from "react-redux";
import { Reducers } from "../../../types/reducers/index.d";
import { MenuNode } from "../../components/main-view/LeftMenuList/menu-tree";

const HOME_ICON = faHome;
const APPROVAL_ICON = faFileSignature;
const STATISTICS_ICON = faChartPie;
const INFO_ICON = faInfoCircle;
const SETTING_ICON = faCog;
const CAR_ICON = faCar;
const RESERVATION_ICON = faClipboardList;

type Props = {
  webMenuAuthList: WebMenuAuthList;
};

const useLeftMenuList = <P extends Partial<Props>>({ webMenuAuthList }: P) => {
  const {
    signInReducer: { loginIdInfo }
  } = useSelector((state: Reducers) => state);
  const makeNewMenu = React.useCallback(() => {
    if (webMenuAuthList) {
      const canManageWorkingSystem =
        loginIdInfo !== Role.USER && webMenuAuthList.workingSystem;
      const menu: MenuNode[] = [
        {
          id: "dashboard",
          kind: "group",
          title: uiString.titles.dashBoard,
          icon: HOME_ICON,
          isActive: webMenuAuthList.dashBoard,
          children: [
            {
              id: "dashboard-personal",
              kind: "leaf",
              title: uiString.titles.dashBoardPersonal,
              pathname: routes.pageRoutes.personal,
              isActive: true
            },
            {
              id: "dashboard-master",
              kind: "leaf",
              title: uiString.titles.dashBoardMaster,
              pathname: routes.pageRoutes.home,
              isActive: true
            }
          ]
        },
        {
          id: "approval-management",
          kind: "group",
          key: "web_approval_flag",
          title: uiString.titles.approvalManagement,
          icon: APPROVAL_ICON,
          isActive: webMenuAuthList.approvalMenu,
          children: [
            {
              id: "approval-dashboard",
              kind: "leaf",
              title: "대시 보드",
              pathname: routes.pageRoutes.approvalDashboard,
              isActive: true
            },
            {
              id: "approval-list",
              kind: "leaf",
              title: "결재 목록",
              pathname: routes.pageRoutes.approvalList,
              isActive: true
            },
            {
              id: "approval-temporary-document",
              kind: "leaf",
              title: "임시 저장 문서",
              pathname: routes.pageRoutes.approvalTemporaryDocument,
              isActive: true
            }
          ]
        },
        {
          id: "statistics",
          kind: "group",
          key: "web_report_flag",
          title: uiString.titles.statistics,
          icon: STATISTICS_ICON,
          isActive: true,
          children: [
            {
              id: "statistics-admin",
              kind: "leaf",
              title: uiString.titles.adminStatistics,
              pathname: routes.pageRoutes.statisticsAdmin,
              isActive: webMenuAuthList.managerStatistics
            },
            {
              id: "statistics-user",
              kind: "leaf",
              title: uiString.titles.userStatistics,
              pathname: routes.pageRoutes.statisticsUser,
              isActive: true
            }
          ]
        },
        {
          id: "work-management-information",
          kind: "leaf",
          key: "web_otmng_flag",
          title: uiString.titles.workManagementInformation,
          icon: INFO_ICON,
          pathname: routes.pageRoutes.workManagementInformation,
          isActive: true
        },
        {
          id: "vehicle-driving-management",
          kind: "leaf",
          key: "web_vehicle_flag",
          title: uiString.titles.vehicleDrivingManagementMenu,
          icon: CAR_ICON,
          pathname: routes.pageRoutes.vehicleDrivingManagement,
          isActive: webMenuAuthList.vehicleDrivingManagementMenu
        },
        {
          id: "reservation-management",
          kind: "leaf",
          key: "web_reserve_flag",
          title: uiString.titles.reservationInformationManagementMenu,
          icon: RESERVATION_ICON,
          pathname: routes.pageRoutes.reservationManagement,
          isActive: webMenuAuthList.reservationManagementMenu
        },
        {
          id: "settings",
          kind: "group",
          title: uiString.titles.configuration,
          icon: SETTING_ICON,
          isActive: true,
          children: [
            {
              id: "settings-basic",
              kind: "leaf",
              title: uiString.titles.basicSetting,
              pathname: routes.pageRoutes.settings,
              isActive: true
            },
            ...(canManageWorkingSystem
              ? [
                  {
                    id: "settings-work",
                    kind: "group" as const,
                    title: "근무 설정",
                    isActive: true,
                    children: [
                      {
                        id: "settings-working-policy",
                        kind: "leaf" as const,
                        title: "근무 정책 관리",
                        pathname: routes.pageRoutes.workingSystem,
                        isActive: true
                      },
                      {
                        id: "settings-work-types",
                        kind: "leaf" as const,
                        title: "근무 유형 관리",
                        pathname: routes.pageRoutes.workTypes,
                        isActive: true
                      }
                    ]
                  }
                ]
              : []),
            {
              id: "settings-annual",
              kind: "leaf",
              title: uiString.titles.annualEnvSetting,
              pathname: routes.pageRoutes.annualEnvSetting,
              isActive:
                loginIdInfo !== Role.USER && webMenuAuthList.annualSetting
            },
            {
              id: "settings-approval",
              kind: "leaf",
              title: "결재 설정",
              pathname: routes.pageRoutes.approvalSetting,
              isActive: webMenuAuthList.approvalMenu
            }
          ]
        }
      ];
      return menu;
    } else {
      return [];
    }
  }, [webMenuAuthList, loginIdInfo]);
  const newMenu = React.useMemo(makeNewMenu, [makeNewMenu]);
  return { newMenu };
};

export default useLeftMenuList;
